import { Component, OnInit, AfterViewInit, ChangeDetectorRef, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { forkJoin } from 'rxjs';
import { FieldWatcherService } from 'app/common/services/field-watcher.service';
import { BaseControl } from 'app/common/components/controls/base-control';
import { MetadataApiService } from 'app/common/services/metadata-api.service';
import { LocalizationManagerService } from 'app/common/services/localization-manager.service';
import { AppSharedStateService } from 'app/common/services/app-shared-state.service';
import { AppSharedUtilityService } from 'app/common/services/app-shared-utility.service';

@Component({
    standalone: false,
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UploadComponent),
            multi: true
        }
    ]
})
export class UploadComponent extends BaseControl implements OnInit, AfterViewInit {
    public allowedExtensions: string[] = [];
    public saveUrl: string;
    public isUploading: boolean = false;
    public selectedFileNames: string = '';
    public uploadError: string = '';

    public get accept(): string {
        return this.allowedExtensions.map((extension: string) => `.${extension}`).join(',');
    }

    // Gets file type display string.
    public get fileTypesDisplay(): string {
        if (!!this.fieldData.metadata.controlOptions && !!this.fieldData.metadata.controlOptions.acceptedFileExtensions) {
            return this.fieldData.metadata.controlOptions.acceptedFileExtensions.replace(/[.,;-]/g, ' ');
        }
        return '';
    }

    // Constructor for UploadComponent.
    constructor(
        protected fieldWatcherService: FieldWatcherService,
        protected metadataApiService: MetadataApiService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected domSanitizer: DomSanitizer,
        protected appSharedStateService: AppSharedStateService,
        protected appSharedUtilityService: AppSharedUtilityService,
        private httpClient: HttpClient,
        public lms: LocalizationManagerService /* Using short name on purpose for data binding. */) {
        super(fieldWatcherService, metadataApiService, changeDetectorRef, domSanitizer, appSharedStateService, appSharedUtilityService, lms);
    }

    // Angular OnInit handler. Part of the lifecycle hooks: https://angular.io/guide/lifecycle-hooks
    public ngOnInit(): void {
        if (!!this.fieldData.metadata && !!this.fieldData.metadata.controlOptions && !!this.fieldData.metadata.controlOptions.acceptedFileExtensions) {
            const delimiters: RegExp = /[.,;-]/;
            this.allowedExtensions = this.fieldData.metadata.controlOptions.acceptedFileExtensions
                .split(delimiters)
                .map((extension: string) => extension.trim().toLowerCase())
                .filter((extension: string) => !!extension);
            this.saveUrl = this.fieldData.metadata.controlOptions.uploadFileUrl;
        }
    }

    public onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        this.uploadError = '';

        if (files.length === 0) {
            this.selectedFileNames = '';
            return;
        }

        const invalidFile = files.find((file: File) => !this.isFileAllowed(file));
        if (invalidFile) {
            this.selectedFileNames = '';
            input.value = '';
            return;
        }

        if (!this.saveUrl) {
            this.uploadError = 'The upload URL is not configured.';
            input.value = '';
            return;
        }

        this.selectedFileNames = files.map((file: File) => file.name).join(', ');
        this.isUploading = true;

        const uploads = files.map((file: File) => {
            const formData = new FormData();
            formData.append('files', file, file.name);
            return this.httpClient.post(this.saveUrl, formData, { responseType: 'text' });
        });

        forkJoin(uploads).subscribe({
            next: () => {
                this.isUploading = false;
            },
            error: (error: unknown) => {
                console.error('File upload failed.', error);
                this.uploadError = 'The file upload failed. Please try again.';
                this.isUploading = false;
            }
        });
    }

    private isFileAllowed(file: File): boolean {
        const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
        if (this.allowedExtensions.length > 0 && !this.allowedExtensions.includes(extension)) {
            this.uploadError = `The file type for "${file.name}" is not supported.`;
            return false;
        }

        const maxFileSize = !!this.fieldData.metadata.controlOptions ? this.fieldData.metadata.controlOptions.maxFileSize : null;
        if (!!maxFileSize && file.size > maxFileSize) {
            this.uploadError = `"${file.name}" exceeds the maximum file size.`;
            return false;
        }

        return true;
    }

    // Angular AfterViewInit handler. Part of the lifecycle hooks: https://angular.io/guide/lifecycle-hooks
    public ngAfterViewInit(): void {
        // This is done in a setTimeout so it defers running until a data binding pass is done first.
        setTimeout(() => {
            // Adding field watchers must be done in ngAfterViewInit.
            this.addCommonFieldWatchers();

            // Mark this control as ready after the field watchers have been set.
            this.fieldData.isControlReady = true;
        });
    }
}
